import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import type { CreateUserRequest } from '../types/admin.types';

interface Props {
  isOpen: boolean;
  isLoading: boolean;
  onSubmit: (data: CreateUserRequest) => Promise<void>;
  onClose: () => void;
}

export const CreateUserModal = ({ isOpen, isLoading, onSubmit, onClose }: Props) => {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    try {
      await onSubmit({ email: email.trim(), name: name.trim() });
      setEmail('');
      setName('');
      onClose();
    } catch (err) {
      const status = (err as Error & { status?: number }).status;
      if (status === 409) {
        setError('A user with this email already exists.');
      } else if (status === 410) {
        setError('This user was recently deleted. To recreate it, please contact the administrator.');
      } else {
        setError('Failed to create user. Please try again.');
      }
    }
  };

  const handleClose = () => {
    if (isLoading) return;
    setEmail('');
    setName('');
    setError(null);
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => { if (!open) handleClose(); }}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>New user</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          <div className="flex flex-col gap-1">
            <label htmlFor="create-email" className="text-sm font-medium text-slate-700">
              Email <span className="text-red-500">*</span>
            </label>
            <input
              id="create-email"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="user@company.com"
              className="rounded-md border border-slate-300 px-3 py-2 text-base outline-none focus:border-slate-500 focus:ring-2 focus:ring-slate-200 transition"
              disabled={isLoading}
            />
          </div>

          <div className="flex flex-col gap-1">
            <label htmlFor="create-name" className="text-sm font-medium text-slate-700">
              Name
            </label>
            <input
              id="create-name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Full name"
              className="rounded-md border border-slate-300 px-3 py-2 text-base outline-none focus:border-slate-500 focus:ring-2 focus:ring-slate-200 transition"
              disabled={isLoading}
            />
          </div>

          {error && (
            <p className="text-sm text-red-600 bg-red-50 rounded-md px-3 py-2">{error}</p>
          )}

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={isLoading}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={isLoading || !email.trim()}>
              {isLoading ? 'Creating…' : 'Create user'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
